import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../api.ts'
import { useAuth } from '../auth/AuthContext.tsx'
import { money, plural, relativeDay, QUOTE_STATUS_LABEL, QUOTE_STATUS_STYLE } from '../format.ts'
import { NewQuoteDialog } from '../components/NewQuoteDialog.tsx'

interface QuoteRow {
  id: string
  number: string | null
  status: string
  customerId: string
  customerName: string
  salesCode: string | null
  title: string | null
  netTotal: string | null
  grossTotal: string | null
  currency: string
  issuedAt: string | null
  validUntil: string | null
  createdAt: string
  approvalRequired: boolean
  isGrantOffer: boolean
}

const STATUS_STYLE = QUOTE_STATUS_STYLE
const STATUS_LABEL = QUOTE_STATUS_LABEL

export function QuotesPage() {
  const { user } = useAuth()
  const nav = useNavigate()
  const [quotes, setQuotes] = useState<QuoteRow[]>([])
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const isOwner = user?.role === 'wlasciciel'

  function reload() {
    const p = new URLSearchParams()
    if (search.trim()) p.set('search', search.trim())
    if (status) p.set('status', status)
    setLoading(true)
    api
      .get<{ quotes: QuoteRow[] }>(`/api/quotes?${p}`)
      .then((r) => {
        setQuotes(r.quotes)
        setError(null)
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Nie udało się pobrać ofert'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const t = setTimeout(reload, 150)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status])

  // Offers waiting on the owner's signature have a client waiting behind them —
  // they go to the top, not into a filter nobody opens.
  const czekaja = quotes.filter((q) => q.status === 'pending_approval')

  return (
    <div className="p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-800">Oferty</h1>
        <button
          onClick={() => setAdding(true)}
          className="text-sm bg-brand-600 text-white px-3 py-1.5 rounded-md hover:bg-brand-700"
        >
          Nowa oferta
        </button>
      </div>

      {isOwner && czekaja.length > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="font-medium text-amber-900 text-sm mb-1.5">
            {czekaja.length} {plural(czekaja.length, 'oferta czeka', 'oferty czekają', 'ofert czeka')} na Twoją akceptację
          </div>
          <ul className="space-y-1">
            {czekaja.map((q) => (
              <li key={q.id} className="text-sm">
                <Link to={`/oferty/${q.id}`} className="text-amber-900 underline font-medium">
                  {q.customerName}
                </Link>
                <span className="text-amber-700">
                  {' '}
                  · {money(q.netTotal, q.currency)} netto · rabat powyżej limitu · {q.salesCode}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-3">
        <input
          type="search"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Szukaj: numer oferty, klient…"
          className="flex-1 min-w-64 px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:border-brand-600"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
        >
          <option value="">Wszystkie</option>
          <option value="szkic">Szkice</option>
          <option value="pending_approval">Czekają na akceptację</option>
          <option value="wyslana">Wysłane</option>
          <option value="zaakceptowana">Zaakceptowane</option>
          <option value="odrzucona">Odrzucone</option>
        </select>
      </div>

      {error && (
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3">
          {error}
        </div>
      )}

      <div className="text-xs text-slate-500 mb-2">
        {loading ? 'Szukam…' : `${quotes.length} ${plural(quotes.length, 'oferta', 'oferty', 'ofert')}`}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase text-slate-500">
              <th className="px-3 py-2 font-medium">Numer</th>
              <th className="px-3 py-2 font-medium">Klient</th>
              <th className="px-3 py-2 font-medium">Przedmiot</th>
              <th className="px-3 py-2 font-medium text-right">Netto</th>
              <th className="px-3 py-2 font-medium">Data</th>
              <th className="px-3 py-2 font-medium">Ważna do</th>
              <th className="px-3 py-2 font-medium">Handlowiec</th>
              <th className="px-3 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {quotes.map((q) => (
              <tr key={q.id} className="border-b border-slate-100 last:border-0 hover:bg-brand-50/50">
                <td className="px-3 py-1.5 font-mono text-xs">
                  <Link to={`/oferty/${q.id}`} className="text-brand-700 hover:underline font-medium">
                    {q.number ?? <span className="text-slate-400 italic font-sans">szkic</span>}
                  </Link>
                </td>
                <td className="px-3 py-1.5">{q.customerName}</td>
                <td className="px-3 py-1.5 text-slate-600 text-xs">
                  {q.title ?? '—'}
                  {q.isGrantOffer && (
                    <span className="ml-1.5 bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded text-[10px]">dotacja</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right tabular">{money(q.netTotal, q.currency)}</td>
                <td className="px-3 py-1.5 text-xs text-slate-500">
                  {q.issuedAt ? relativeDay(q.issuedAt) : relativeDay(q.createdAt)}
                </td>
                <td className="px-3 py-1.5 text-xs text-slate-500">
                  {q.validUntil ? new Date(q.validUntil).toLocaleDateString('pl-PL') : '—'}
                </td>
                <td className="px-3 py-1.5 text-xs font-mono text-slate-500">{q.salesCode ?? '—'}</td>
                <td className="px-3 py-1.5">
                  <span className={`text-xs px-1.5 py-0.5 rounded whitespace-nowrap ${STATUS_STYLE[q.status] ?? ''}`}>
                    {STATUS_LABEL[q.status] ?? q.status}
                  </span>
                </td>
              </tr>
            ))}
            {!loading && quotes.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-slate-400">
                  {search || status ? 'Nic nie znaleziono.' : 'Brak ofert — wystaw pierwszą.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {adding && (
        <NewQuoteDialog onClose={() => setAdding(false)} onCreated={(id) => nav(`/oferty/${id}/edytuj`)} />
      )}
    </div>
  )
}
